import { useEffect, useState } from "react";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import { Download, Copy, Check, FileText, Package } from "lucide-react";

const BRAND = `${process.env.REACT_APP_BACKEND_URL}/api/brand-static`;
const PRODUCTS_STATIC = `${process.env.REACT_APP_BACKEND_URL}/api/products-static`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOUSE_LOGOS = [
  { name: "Stacked lockup", file: "lockup-stacked", dark: false },
  { name: "Stacked lockup — reverse", file: "lockup-stacked-light", dark: true },
  { name: "Horizontal lockup", file: "lockup-horizontal", dark: false },
  { name: "Horizontal lockup — reverse", file: "lockup-horizontal-light", dark: true },
  { name: "Wordmark", file: "wordmark", dark: false },
  { name: "Wordmark — copper", file: "wordmark-copper", dark: false },
  { name: "The mark", file: "mark", dark: false },
  { name: "The mark — reverse", file: "mark-reverse", dark: true },
  { name: "App / WhatsApp icon", file: "app-icon", dark: false },
  { name: "App icon — copper", file: "app-icon-copper", dark: false },
];

const PALETTE = [
  { name: "Ink", hex: "#101010", note: "Primary · mono reproduction" },
  { name: "Ivory", hex: "#F3EFE6", note: "Reverse · on ink & photos" },
  { name: "Copper", hex: "#B87333", note: "Accent · foil & stamping" },
];

const Section = ({ title, kicker, children }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
    <p
      className="text-xs uppercase tracking-[0.2em] font-semibold mb-3"
      style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
    >
      {kicker}
    </p>
    <h2
      className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-10"
      style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const AssetCard = ({ title, preview, previewDark, links, testId }) => (
  <div
    className="rounded-sm border border-[var(--mp-line)] overflow-hidden flex flex-col product-card"
    style={{ backgroundColor: "var(--mp-card)" }}
    data-testid={testId}
  >
    <div
      className="h-32 flex items-center justify-center p-6"
      style={{ backgroundColor: previewDark ? "#101010" : "#F3EFE6" }}
    >
      <img src={preview} alt={title} className="max-h-full max-w-full object-contain" loading="lazy" />
    </div>
    <div className="p-5 flex flex-col gap-3 flex-1">
      <p
        className="text-sm font-medium"
        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
      >
        {title}
      </p>
      <div className="mt-auto flex flex-wrap gap-2">
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            download
            className="text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors"
            style={{ borderColor: "var(--mp-line)", color: "var(--mp-copper)" }}
            data-testid={`${testId}-${l.label.toLowerCase()}`}
          >
            {l.label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

export default function BrandKitPage() {
  const [products, setProducts] = useState([]);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then(setProducts)
      .catch((e) => console.debug("Products load failed:", e));
  }, []);

  const copyHex = (hex) => {
    // writeText returns a Promise — a sync try/catch would miss NotAllowedError
    navigator.clipboard
      ?.writeText(hex)
      .catch((e) => console.debug("Clipboard write blocked:", e));
    setCopied(hex);
    setTimeout(() => setCopied(""), 1600);
  };

  return (
    <PageLayout>
      <SEO
        title="Brand Kit & Quality Marks — Logos, Seals, Labels | Mutha Paras & Co."
        description="Download the Mutha Paras & Co. brand kit: house logos (SVG/PNG), all 9 product seals, specification labels, printable 50 mm label sheets and the quality marks sheet."
        path="/brand-kit"
      />

      {/* Hero */}
      <section
        className="px-6 lg:px-10 py-20 lg:py-28"
        style={{ backgroundColor: "var(--mp-solid)" }}
        data-testid="brand-kit-hero"
      >
        <div className="max-w-7xl mx-auto">
          <img
            src="/brand/lockup-stacked-light.svg"
            alt="Mutha Paras & Co."
            className="w-[240px] sm:w-[320px] h-auto mb-10 animate-fade-in-up"
          />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium max-w-3xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "#F3EFE6" }}
            data-testid="brand-kit-heading"
          >
            Brand Kit &amp; Quality Marks
          </h1>
          <p
            className="mt-6 text-base md:text-lg max-w-2xl leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif", color: "rgba(243,239,230,0.75)" }}
          >
            Everything a buyer, printer or packaging unit needs — house logos, the nine
            product seals, specification labels and print-ready 50 mm label sheets.
            Free to use for Mutha Paras &amp; Co. stock, exactly as supplied.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={`${BRAND}/mutha-paras-brand-kit.zip`}
              download
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              style={{ backgroundColor: "var(--mp-copper)", color: "#101010", fontFamily: "'Manrope', sans-serif" }}
              data-testid="brand-kit-download-all"
            >
              <Package className="w-4 h-4" /> Download all (ZIP)
            </a>
            <a
              href={`${BRAND}/brand-usage-guide.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold border"
              style={{ borderColor: "rgba(243,239,230,0.4)", color: "#F3EFE6", fontFamily: "'Manrope', sans-serif" }}
              data-testid="brand-kit-usage-guide"
            >
              <FileText className="w-4 h-4" /> Usage guide (A4 PDF)
            </a>
          </div>
        </div>
      </section>

      {/* Palette */}
      <Section kicker="The palette" title="Ink, Ivory & Copper">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="brand-kit-palette">
          {PALETTE.map((c) => (
            <button
              key={c.hex}
              onClick={() => copyHex(c.hex)}
              className="text-left rounded-sm border border-[var(--mp-line)] overflow-hidden"
              style={{ backgroundColor: "var(--mp-card)" }}
              data-testid={`palette-${c.name.toLowerCase()}`}
            >
              <div className="h-24" style={{ backgroundColor: c.hex }} />
              <div className="p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--mp-text)" }}>
                    {c.name} · {c.hex}
                  </p>
                  <p className="text-xs mt-1" style={{ color: "var(--mp-muted)" }}>
                    {c.note}
                  </p>
                </div>
                {copied === c.hex ? (
                  <Check className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
                ) : (
                  <Copy className="w-4 h-4" style={{ color: "var(--mp-muted)" }} />
                )}
              </div>
            </button>
          ))}
        </div>
      </Section>

      {/* House logos */}
      <section style={{ backgroundColor: "var(--mp-surface)" }}>
        <Section kicker="House marks" title="Logos & lockups">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="brand-kit-logos">
            {HOUSE_LOGOS.map((l) => (
              <AssetCard
                key={l.file}
                title={l.name}
                preview={`${BRAND}/${l.file}.svg`}
                previewDark={l.dark}
                testId={`logo-${l.file}`}
                links={[
                  { label: "SVG", href: `${BRAND}/${l.file}.svg` },
                  ...(l.file === "mark" || l.file === "wordmark" || l.file.startsWith("lockup")
                    ? [{ label: "PNG", href: `${BRAND}/${l.file.replace("-light", "-reverse")}.png` }]
                    : []),
                ]}
              />
            ))}
          </div>
        </Section>
      </section>

      {/* Product seals + labels */}
      <Section kicker="Quality marks" title="The nine product seals">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="brand-kit-seals">
          {products.map((p) => (
            <AssetCard
              key={p.id}
              title={p.name}
              preview={`${PRODUCTS_STATIC}/logos/${p.id}.png`}
              testId={`seal-${p.id}`}
              links={[
                { label: "SVG", href: `${PRODUCTS_STATIC}/logos/${p.id}.svg` },
                { label: "PNG", href: `${PRODUCTS_STATIC}/logos/${p.id}.png` },
                { label: "Label", href: `${PRODUCTS_STATIC}/labels/${p.id}.png` },
                { label: "50mm A4", href: `${PRODUCTS_STATIC}/print/${p.id}_labels_50mm_A4.pdf` },
              ]}
            />
          ))}
        </div>
      </Section>

      {/* Print sheets */}
      <section style={{ backgroundColor: "var(--mp-surface)" }}>
        <Section kicker="Print ready" title="Sheets for the press">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="brand-kit-print">
            {[
              {
                title: "Quality marks sheet — all 9 seals",
                note: "A4, 58 mm seals, 3 × 3. For shade books, rate cards and counter display.",
                href: `${PRODUCTS_STATIC}/print/quality_marks_sheet_A4.pdf`,
                testId: "print-marks-sheet",
              },
              {
                title: "Rate card — all 9 qualities",
                note: "A4 with every seal, width, panna, fold, cut pack and current shade count, plus a blank rate column for your team.",
                href: `${PRODUCTS_STATIC}/print/rate_card_A4.pdf`,
                testId: "print-rate-card",
              },
              {
                title: "Brand usage guide",
                note: "One page: clear space, minimum sizes, lockup choice, palette and the never-do list.",
                href: `${BRAND}/brand-usage-guide.pdf`,
                testId: "print-usage-guide",
              },
            ].map((s) => (
              <a
                key={s.testId}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-sm border border-[var(--mp-line)] p-7 flex items-start gap-5 product-card"
                style={{ backgroundColor: "var(--mp-card)" }}
                data-testid={s.testId}
              >
                <FileText className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--mp-copper)" }} />
                <div>
                  <p className="text-base font-medium" style={{ color: "var(--mp-text)" }}>
                    {s.title}
                  </p>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--mp-muted)" }}>
                    {s.note}
                  </p>
                  <span
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mt-4"
                    style={{ color: "var(--mp-copper)" }}
                  >
                    <Download className="w-3.5 h-3.5" /> Open PDF
                  </span>
                </div>
              </a>
            ))}
          </div>
        </Section>
      </section>

      {/* Usage note */}
      <Section kicker="Please note" title="How to use these marks">
        <ul
          className="space-y-4 max-w-3xl text-sm leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
          data-testid="brand-kit-rules"
        >
          {[
            "Keep clear space of 15% of the mark height on all four sides.",
            "Minimum sizes: full lockup 80 mm / 400 px, the mark alone 8 mm / 24 px.",
            "Reproduce in one ink. No gradients, glows, bevels or drop shadows.",
            "Never re-type the wordmark, box the mark, stretch it or re-colour it.",
            "On photographs or dark packaging, use the ivory reverse version.",
            "Product seals identify our stock — please do not apply them to other goods.",
          ].map((r) => (
            <li key={r} className="flex gap-3">
              <span style={{ color: "var(--mp-copper)" }}>·</span>
              {r}
            </li>
          ))}
        </ul>
      </Section>
    </PageLayout>
  );
}
